import Konva from 'konva'

import HaspObject from './HaspObject'
import CustomArc from '../draw/CustomArc'
import { processIconText } from '../utils/iconFonts'
import { FontBinding } from '../services/FontBinding.js'
import { useFontStore } from '../stores/fontStore.js'
import { getBadgeGeometry, getBadgeLayout } from '../draw/badgeGeometry'
import { calculateRingArcs, normalizeRingSegment, updateRingSegment as patchRingSegment } from '../draw/badgeRing'
import { resolvePadding, onPaddingStyle } from './properties/v2/padding.js'
import { splitColorOpa } from '../utils/color.js'
import { getObjectPage } from '../utils/hasputils'
import { BADGE_CONNECTION_SIDES, badgeConnectionHandleConfig } from '../draw/badgeConnectionInteraction'
import { resolveBadgeRing } from '../draw/badgeRingResolver'

export default class HaspBadge extends HaspObject {
    constructor(config) {
        if (config.icon !== undefined && config.text === undefined) config.text = config.icon
        if (config.val !== undefined && config.value === undefined) config.value = config.val
        config.text ??= 'Net'
        config.value = Number.isFinite(Number(config.value)) ? Number(config.value) : 0
        delete config.icon
        delete config.val
        super(config)

        this.ringGroup = new Konva.Group({ listening: false, obj: 'badge_ring' })
        this.badgeText = this._textNode('badge_text')
        this.primaryValueText = this._textNode('badge_value')
        this.contentTextNodes = [this.badgeText, this.primaryValueText]
        this.add(this.ringGroup, ...this.contentTextNodes)

        this._textFontBinding = new FontBinding(family => this._setFontFamily(this.badgeText, family))
        this._valueFontBinding = new FontBinding(family => this._setFontFamily(this.primaryValueText, family))

        this.on('transform', () => this.layoutBadge(true))
        this.on('transformend', () => this.layoutBadge(true))
        for (const prop of ['radius', 'bg_color', 'border_color', 'border_width', 'border_side', 'outline_width',
            'outline_color', 'outline_pad', 'shadow_width', 'shadow_spread', 'shadow_ofs_x', 'shadow_ofs_y',
            'shadow_color', 'shadow_opa', 'text_color', 'text_font', 'text_font_family', 'text_letter_space',
            'text_line_space', 'text_opa']) {
            this.onStyle('main', prop, () => this.layoutBadge())
        }
        onPaddingStyle(this, 'main', () => this.layoutBadge())
        this.on('text_text', () => this.layoutBadge())
        this.on('data_value', () => this.layoutBadge())
        this.on('value', () => {
            this.value = Number.isFinite(Number(this.value)) ? Number(this.value) : 0
            this.layoutBadge()
            this.getLayer()?.refreshConnectionsForBadgeValue?.(this)
        })
        this.on('value_str', () => this.layoutBadge())
        this.on('text_font_resource', () => this._applyTextFont())
        this.on('value_font_resource', () => this._applyValueFont())
        for (const property of ['text_font', 'text_ofs_x', 'text_ofs_y',
            'value_font', 'value_ofs_x', 'value_ofs_y']) {
            this.on(property, () => this.layoutBadge())
        }
        this.on('ring_width', () => this.layoutBadge())
        this.on('ring_start_angle', () => this.layoutBadge())
        this.on('ring_segments', () => {
            this._syncRingHassProperties()
            this.layoutBadge()
        })
        this.on('ring_source', () => {
            this._syncRingHassProperties()
            this.layoutBadge()
        })
        this.on('ring_reference', () => this.layoutBadge())
        this.on('font', () => this.layoutBadge())
        this.on('destroy', () => {
            this._textFontBinding.dispose()
            this._valueFontBinding.dispose()
        })

        this.connectionHandles = new Konva.Group({ visible: false, obj: 'badge_connection_handles' })
        for (const side of BADGE_CONNECTION_SIDES) {
            const handle = new Konva.Circle(badgeConnectionHandleConfig(side))
            handle.on('dragstart', event => {
                event.cancelBubble = true
                this.getLayer()?.startBadgeConnectionDrag(this, side, handle)
            })
            handle.on('dragmove', event => {
                event.cancelBubble = true
                this.getLayer()?.updateBadgeConnectionDrag(handle)
            })
            handle.on('dragend', event => {
                event.cancelBubble = true
                this.getLayer()?.finishBadgeConnectionDrag(handle)
            })
            this.connectionHandles.add(handle)
        }
        this.add(this.connectionHandles)

        this.draggable(true)
        this._syncRingHassProperties()
        this.layoutBadge()
    }

    _syncRingHassProperties() {
        const segmentPrefix = 'ring_segment.'
        const regular = (this.hass_properties ?? []).filter(property => !property.startsWith(segmentPrefix))
        const segmentProperties = this.ring_source === 'manual'
            ? (this.ring_segments ?? [])
                .filter(segment => segment.id && !String(segment.id).includes('.'))
                .map(segment => `${segmentPrefix}${segment.id}.value`)
            : []
        this.hass_properties = [...new Set([...regular, ...segmentProperties])]
    }

    _textNode(obj) {
        const node = new Konva.Text({
            listening: true, draggable: true, align: 'center', verticalAlign: 'middle', wrap: 'word', obj,
        })
        node.on('dragmove', () => {
            this.textBounds(node)
            this.storeTextOffset(node, false)
        })
        node.on('dragend', () => {
            this.textBounds(node)
            this.storeTextOffset(node, true)
        })
        return node
    }

    textBounds(node) {
        if (!node) return
        if (node.x() < 0) node.x(0)
        if (node.x() > this.width() - node.width()) node.x(this.width() - node.width())
        if (node.y() < 0) node.y(0)
        if (node.y() > this.height() - node.height()) node.y(this.height() - node.height())
    }

    textOffset(node) {
        return {
            x: node.x() + (node.width() / 2) - (this.width() / 2),
            y: node.y() + (node.height() / 2) - (this.height() / 2),
        }
    }

    contentLayout() {
        const main = this.style('main')
        const padding = resolvePadding(main)
        const ringInset = Math.max(0, Number(this.ring_width) || 0)
        return getBadgeLayout(
            { x: 0, y: 0, width: this.width(), height: this.height() },
            Math.max(padding.top, padding.right, padding.bottom, padding.left) + ringInset,
            Math.max(Number(main.text_font) || 16, Number(this.value_font) || 16),
        )
    }

    storeTextOffset(node, notify = true) {
        const layout = this.contentLayout()
        if (node === this.badgeText) {
            this.text_ofs_x = node.x() - layout.icon.x
            this.text_ofs_y = node.y() - layout.icon.y
            if (notify) this.adjust('text_offset')
        } else if (node === this.primaryValueText) {
            this.value_ofs_x = node.x() - layout.text.x
            this.value_ofs_y = node.y() - layout.text.y
            if (notify) this.adjust('value_offset')
        }
    }

    _setFontFamily(node, family) {
        node.fontFamily(family)
        node.getLayer()?.batchDraw()
    }

    _fontResource(property) {
        const file = this[property]
        return file ? useFontStore().fonts.find(font => font.file === file) : null
    }

    _textFontResource() {
        return this._fontResource('text_font_resource')
    }

    _valueFontResource() {
        return this._fontResource('value_font_resource')
    }

    _applyTextFont() {
        this._textFontBinding.set(this._textFontResource(), this.style('main').text_font_family || 'Roboto Condensed')
    }

    _applyValueFont() {
        this._valueFontBinding.set(this._valueFontResource(), 'Material Icons')
    }

    getResizeMode() {
        return 'keep_ratio'
    }

    getConnectionGeometry() {
        const page = getObjectPage(this)
        const position = page ? this.getAbsolutePosition(page) : this.position()
        return getBadgeGeometry({
            x: position.x,
            y: position.y,
            width: this.width(),
            height: this.height(),
        })
    }

    setSelected(selected) {
        this.connectionHandles?.visible(selected)
        this.layoutConnectionHandles()
    }

    layoutConnectionHandles() {
        if (!this.connectionHandles) return
        const geometry = getBadgeGeometry({ x: 0, y: 0, width: this.width(), height: this.height() })
        const positions = {
            top: { x: geometry.centerX, y: geometry.centerY - geometry.radius },
            right: { x: geometry.centerX + geometry.radius, y: geometry.centerY },
            bottom: { x: geometry.centerX, y: geometry.centerY + geometry.radius },
            left: { x: geometry.centerX - geometry.radius, y: geometry.centerY },
        }
        this.connectionHandles.getChildren().forEach(handle => handle.position(positions[handle.getAttr('side')]))
        this.connectionHandles.moveToTop()
    }

    setRingSegments(segments) {
        this.ring_segments = (Array.isArray(segments) ? segments : []).map(normalizeRingSegment)
        this.adjust('ring_segments')
    }

    updateRingSegment(id, patch) {
        this.ring_segments = patchRingSegment(this.ring_segments, id, patch)
        this.adjust('ring_segments')
        return this.ring_segments.find(segment => segment.id === id)
    }

    getResolvedRingSegments() {
        const page = getObjectPage(this)
        const connections = page?.find?.(node => node.obj === 'connection') ?? []
        return resolveBadgeRing({
            id: this.id(), value: this.value, ring_source: this.ring_source,
            ring_reference: this.ring_reference, ring_segments: this.ring_segments,
            border_color: this.styleProp('main', 'border_color'),
        }, connections)
    }

    _drawRing(layout) {
        this.ringGroup.destroyChildren()
        const width = Math.max(0, Number(this.ring_width) || 0)
        if (width <= 0) return
        const outerRadius = Math.max(0, layout.circle.radius - width / 2)
        const innerRadius = Math.max(0, outerRadius - width)
        for (const segment of calculateRingArcs(this.getResolvedRingSegments(), this.ring_start_angle)) {
            const color = splitColorOpa(segment.color)
            const arc = new CustomArc({
                x: layout.circle.centerX,
                y: layout.circle.centerY,
                innerRadius,
                outerRadius,
                startAngle: segment.startAngle,
                endAngle: segment.endAngle,
                fill: color.color,
                lineRounded: 'square',
                listening: false,
                obj: 'badge_ring_segment',
            })
            arc.opacity(color.opacity)
            this.ringGroup.add(arc)
        }
    }

    layoutBadge(refreshConnections = false) {
        const main = this.style('main')
        const layout = this.contentLayout()
        const textColor = splitColorOpa(main.text_color || '#ffffffff')

        this._drawRing(layout)
        this.fire('ring-resolved')
        this.badgeText.setAttrs({
            ...layout.icon,
            x: layout.icon.x + (Number(this.text_ofs_x) || 0),
            y: layout.icon.y + (Number(this.text_ofs_y) || 0),
            text: processIconText(this.text || ''),
            fill: textColor.color, opacity: textColor.opacity,
            fontSize: Number(main.text_font) || layout.icon.fontSize,
            lineHeight: 1 + (Number(main.text_line_space) || 0) / Math.max(1, Number(main.text_font) || layout.icon.fontSize),
            letterSpacing: Number(main.text_letter_space) || 0,
        })
        this.primaryValueText.setAttrs({
            ...layout.text,
            x: layout.text.x + (Number(this.value_ofs_x) || 0),
            y: layout.text.y + (Number(this.value_ofs_y) || 0),
            text: processIconText(this.value_str || ''),
            fill: textColor.color, opacity: textColor.opacity,
            fontSize: Number(this.value_font) || Number(main.text_font) || 16,
            lineHeight: 1 + (Number(main.text_line_space) || 0) / Math.max(1, Number(this.value_font) || Number(main.text_font) || 16),
            letterSpacing: Number(main.text_letter_space) || 0,
        })
        this.badgeText.visible(Boolean(this.text))
        this.primaryValueText.visible(Boolean(this.value_str))

        this._applyTextFont()
        this._applyValueFont()
        this.idTextObj.moveToTop()
        this.layoutConnectionHandles()
        if (refreshConnections) this.getLayer()?.refreshBadgeConnections?.()
    }

    objectExport(page, objectData, hassConfig) {
        super.objectExport(page, objectData, hassConfig)
        if (this.text) {
            const offset = this.textOffset(this.badgeText)
            objectData.text_ofs_x = offset.x
            objectData.text_ofs_y = offset.y
        }
        if (this.value_str !== '') {
            const offset = this.textOffset(this.primaryValueText)
            objectData.value_ofs_x = offset.x
            objectData.value_ofs_y = offset.y
        }
    }
}
